import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { RefreshCw, CheckCircle2, XCircle } from "lucide-react";
import {
  fetchWhatsAppAppStatus,
  checkWhatsAppNumber,
} from "../../api/notifications/notificationAppsApi";
import {
  listNotificationTemplates,
  listNotificationTemplatesPortal,
  sendNotificationToStudent,
  sendNotificationToStudentPortal,
  sendReceiptNotification,
  sendReceiptNotificationPortal,
  getStudentFeeLedger,
  getStudentFeeLedgerPortal,
} from "../../api/fees/feeApi";
import {
  Modal,
  Button,
  Select,
  Badge,
  Alert,
  Skeleton,
  Table,
} from "../../components/ui";
import { useToast } from "../../context/ToastContext";

const EVENT_OPTIONS = [
  { value: "DUE_REMINDER", label: "Due reminder" },
  { value: "OVERDUE_REMINDER", label: "Overdue reminder" },
  { value: "RECEIPT", label: "Receipt" },
  { value: "CUSTOM", label: "Custom" },
];

const currency = (n) => `₹${Number(n || 0).toFixed(2)}`;

/**
 * Mirrors backend utils/normalizePhone.js: strips non-digits, and defaults
 * a bare 10-digit number to India's country code ("91"), since numbers are
 * commonly stored/entered without one but WhatsApp requires it.
 */
const normalizePhone = (raw) => {
  const digits = String(raw || "").replace(/\D/g, "");
  if (!digits) return "";
  if (digits.length === 10) return `91${digits}`;
  return digits;
};

/**
 * Mirrors backend renderTemplate() in notificationSendControllers.js so the
 * preview shown here matches what will actually be sent. This is a PREVIEW
 * ONLY — the authoritative render still happens server-side at send time.
 */
const renderPreview = (body, data = {}) => {
  let out = body || "";
  for (const [key, value] of Object.entries(data)) {
    const re = new RegExp(`\\{\\{${key}\\}\\}`, "gi");
    out = out.replace(re, value != null ? String(value) : "");
  }
  return out;
};

const ChannelSendPanel = ({
  isOpen,
  onClose,
  channel = "WHATSAPP",
  studentId,
  student = null,
  receiptId = null,
  isPortal = false,
  defaultEventType = "DUE_REMINDER",
  onSuccess,
}) => {
  const [appStatus, setAppStatus] = useState("DISCONNECTED");
  const [checking, setChecking] = useState(false);
  const [eventType, setEventType] = useState(defaultEventType);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [ledgerRows, setLedgerRows] = useState([]);
  const [loadingLedger, setLoadingLedger] = useState(false);
  const [sending, setSending] = useState(false);
  const [results, setResults] = useState(null);
  const toast = useToast();

  // { status: 'idle' | 'checking' | 'exists' | 'not_found' | 'error', jid?, message? }
  const [waCheck, setWaCheck] = useState({ status: "idle" });

  const channelLabel =
    channel === "WHATSAPP" ? "WhatsApp" : channel === "SMS" ? "SMS" : "Email";

  const connectPath = isPortal ? "/portal/whatsapp" : "/owner/whatsapp";
  const hubPath = isPortal ? "/staff-panel/notification-apps" : "/notification-apps";

  // LOG PROPS WHEN PANEL OPENS
  useEffect(() => {
    if (isOpen) {
      console.log("🔍 [DEBUG - SendPanel] Drawer Opened with Props:", {
        isOpen,
        channel,
        studentId,
        studentIdType: typeof studentId,
        studentObj: student,
        receiptId,
        isPortal,
        defaultEventType,
      });
    }
  }, [isOpen, channel, studentId, student, receiptId, isPortal, defaultEventType]);

  const checkConnection = useCallback(async () => {
    if (channel !== "WHATSAPP") {
      setAppStatus("DISCONNECTED");
      return;
    }
    setChecking(true);
    console.log("🔍 [DEBUG - SendPanel] Checking WA connection status...");
    try {
      const res = await fetchWhatsAppAppStatus(isPortal);
      console.log("🔍 [DEBUG - SendPanel] WA Connection Res:", res);
      setAppStatus(res.status || "DISCONNECTED");
    } catch (err) {
      console.error("❌ [DEBUG - SendPanel] WA Connection Check Error:", err);
      setAppStatus("DISCONNECTED");
    } finally {
      setChecking(false);
    }
  }, [channel, isPortal]);

  const loadTemplates = useCallback(async () => {
    if (channel !== "WHATSAPP") return;
    setLoadingTemplates(true);
    console.log("🔍 [DEBUG - SendPanel] Loading templates for:", { eventType, isPortal });
    try {
      const params = { eventType, isActive: "true", channel: "WHATSAPP" };
      const res = isPortal
        ? await listNotificationTemplatesPortal(params)
        : await listNotificationTemplates(params);
      console.log("🔍 [DEBUG - SendPanel] Templates loaded res:", res);
      setTemplates(res.data || res || []);
    } catch (err) {
      console.error("❌ [DEBUG - SendPanel] Template Load Error:", err);
      toast.error(err.response?.data?.message || "Failed to load templates");
    } finally {
      setLoadingTemplates(false);
    }
  }, [channel, eventType, isPortal]);

  const loadLedger = useCallback(async () => {
    if (channel !== "WHATSAPP" || receiptId || !studentId) {
      console.log("🔍 [DEBUG - SendPanel] Skipping ledger fetch:", { channel, receiptId, studentId });
      setLedgerRows([]);
      return;
    }
    setLoadingLedger(true);
    console.log("🔍 [DEBUG - SendPanel] Fetching ledger for studentId:", studentId);
    try {
      const res = isPortal
        ? await getStudentFeeLedgerPortal(studentId)
        : await getStudentFeeLedger(studentId);

      console.log("🔍 [DEBUG - SendPanel] Raw Ledger Res:", res);

      const list =
        res?.data?.ledger ||
        res?.ledger ||
        res?.data ||
        res ||
        [];

      console.log("🔍 [DEBUG - SendPanel] Extracted Ledger Array:", list);
      setLedgerRows(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error("❌ [DEBUG - SendPanel] Ledger Fetch Error:", err);
      setLedgerRows([]);
    } finally {
      setLoadingLedger(false);
    }
  }, [channel, receiptId, studentId, isPortal]);

  useEffect(() => {
    if (!isOpen) return;
    setEventType(receiptId ? "RECEIPT" : defaultEventType);
    setResults(null);
    setSelectedTemplateId("");
    checkConnection();
  }, [isOpen, channel, receiptId, defaultEventType, checkConnection]);

  useEffect(() => {
    if (!isOpen || channel !== "WHATSAPP") return;
    loadTemplates();
    loadLedger();
  }, [isOpen, channel, loadTemplates, loadLedger]);

  useEffect(() => {
    if (!templates.length) {
      setSelectedTemplateId("");
      return;
    }
    if (!templates.some((t) => t._id === selectedTemplateId)) {
      setSelectedTemplateId(templates[0]._id);
    }
  }, [templates, selectedTemplateId]);

  const phone = student?.primaryContact || student?.secondaryContact || "";
  const email = student?.email || "";
  const normalizedPhone = useMemo(() => normalizePhone(phone), [phone]);

  const contactOk = () => {
    if (channel === "WHATSAPP" || channel === "SMS") {
      return normalizedPhone.length >= 11;
    }
    if (channel === "EMAIL") {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email || ""));
    }
    return false;
  };

  const runWaCheck = useCallback(async () => {
    if (channel !== "WHATSAPP" || !normalizedPhone || appStatus !== "CONNECTED") {
      setWaCheck({ status: "idle" });
      return;
    }
    setWaCheck({ status: "checking" });
    console.log("🔍 [DEBUG - SendPanel] Checking WhatsApp existence for:", normalizedPhone);
    try {
      const res = await checkWhatsAppNumber(normalizedPhone, isPortal);
      console.log("🔍 [DEBUG - SendPanel] WA existence check res:", res);
      setWaCheck(
        res.exists
          ? { status: "exists", jid: res.jid }
          : { status: "not_found" }
      );
    } catch (err) {
      console.error("❌ [DEBUG - SendPanel] WA existence check error:", err);
      setWaCheck({
        status: "error",
        message: err.response?.data?.message || "Could not verify this number right now",
      });
    }
  }, [channel, normalizedPhone, appStatus, isPortal]);

  useEffect(() => {
    if (!isOpen) return;
    runWaCheck();
  }, [isOpen, runWaCheck]);

  const canSend =
    channel === "WHATSAPP" &&
    appStatus === "CONNECTED" &&
    contactOk() &&
    waCheck.status !== "not_found" &&
    !!studentId;

  const name = student
    ? [student.firstName, student.middleName, student.lastName].filter(Boolean).join(" ")
    : studentId;

  const dueLedgerRows = useMemo(
    () =>
      ledgerRows
        .filter((l) => l.isPublished && Number(l.totalDue) > 0)
        .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate)),
    [ledgerRows]
  );

  const outstandingTotal = useMemo(
    () =>
      Math.round(
        dueLedgerRows.reduce((sum, l) => sum + Number(l.totalDue || 0), 0) * 100
      ) / 100,
    [dueLedgerRows]
  );

  const nextDueDate = dueLedgerRows[0]?.dueDate
    ? new Date(dueLedgerRows[0].dueDate).toLocaleDateString()
    : "";

  const previewData = {
    studentName: name,
    admissionNo: student?.admissionNo || "",
    amount: outstandingTotal.toFixed(2),
    outstanding: outstandingTotal.toFixed(2),
    dueDate: nextDueDate,
  };

  const templatesToPreview = selectedTemplateId
    ? templates.filter((t) => t._id === selectedTemplateId)
    : templates;

  const handleSend = async () => {
    console.log("🚀 [DEBUG - SendPanel] Send button clicked!");
    setResults(null);

    console.log("🔍 [DEBUG - SendPanel] Send Diagnostics:", {
      channel,
      appStatus,
      contactOk: contactOk(),
      rawPhone: phone,
      normalizedPhone,
      waCheckStatus: waCheck.status,
      studentId,
      studentIdType: typeof studentId,
      receiptId,
      isPortal,
      selectedTemplateId,
      eventType,
    });

    if (channel !== "WHATSAPP") {
      toast.error(`${channelLabel} is not available yet. Connect it from Notification apps when ready.`);
      return;
    }
    if (appStatus !== "CONNECTED") {
      toast.error("WhatsApp is not connected. Open Notification apps → Connect WhatsApp.");
      return;
    }
    if (!contactOk()) {
      toast.error("Student has no valid phone number for WhatsApp.");
      return;
    }
    if (waCheck.status === "not_found") {
      toast.error("This number is not registered on WhatsApp.");
      return;
    }

    setSending(true);
    try {
      let res;
      if (receiptId) {
        const payload = {
          receiptId,
          studentId,
          channel: "WHATSAPP",
        };
        console.log("🔍 [DEBUG - SendPanel] Sending RECEIPT notification with payload:", payload);

        res = isPortal
          ? await sendReceiptNotificationPortal(payload)
          : await sendReceiptNotification(payload);
      } else {
        const payload = { studentId, eventType, channel: "WHATSAPP" };
        if (selectedTemplateId) payload.templateId = selectedTemplateId;

        console.log("🔍 [DEBUG - SendPanel] Sending STUDENT notification with payload:", payload);

        res = isPortal
          ? await sendNotificationToStudentPortal(payload)
          : await sendNotificationToStudent(payload);
      }

      console.log("✅ [DEBUG - SendPanel] Send Response Success:", res);

      const list = res?.data || res || [];
      setResults(Array.isArray(list) ? list : [list]);
      if (onSuccess) onSuccess(res);
    } catch (err) {
      console.error("❌ [DEBUG - SendPanel] Send API Error Caught:", {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
      });
      toast.error(err.response?.data?.message || "Send failed");
    } finally {
      setSending(false);
    }
  };

  // Compact responsive columns with text truncation to prevent overflow
  const ledgerColumns = [
    {
      key: "title",
      header: "Bill",
      render: (l) => (
        <span
          className="block truncate text-neutral-800 dark:text-neutral-100 max-w-[110px] sm:max-w-none"
          title={l.title || `${l.month || ""} ${l.year || ""}`}
        >
          {l.title || `${l.month || ""} ${l.year || ""}`}
        </span>
      ),
    },
    {
      key: "dueDate",
      header: "Due Date",
      nowrap: true,
      render: (l) => (
        <span className="text-[11px] text-neutral-600 dark:text-neutral-300">
          {l.dueDate ? new Date(l.dueDate).toLocaleDateString() : "—"}
        </span>
      ),
    },
    {
      key: "status",
      header: "Status",
      render: (l) => (
        <Badge variant="default" className="text-[10px] px-1.5 py-0.5">
          {l.status}
        </Badge>
      ),
    },
    {
      key: "totalDue",
      header: "Due Amount",
      align: "right",
      nowrap: true,
      render: (l) => (
        <span className="font-semibold text-neutral-900 dark:text-neutral-100 text-xs">
          {currency(l.totalDue)}
        </span>
      ),
    },
  ];

  const templateSelectOptions = [
    {
      value: "",
      label: loadingTemplates
        ? "Loading templates…"
        : templates.length
        ? "All active templates for this event"
        : "No active templates found",
    },
    ...templates.map((t) => ({ value: t._id, label: t.name })),
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Send via ${channelLabel}`}
      className="w-full max-w-lg lg:max-w-xl"
      footer={
        <>
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={sending}
          >
            Close
          </Button>
          <Button
            type="button"
            variant="primary"
            isLoading={sending}
            disabled={channel === "WHATSAPP" && !canSend}
            onClick={handleSend}
          >
            Send {channelLabel}
          </Button>
        </>
      }
    >
      {/* Scrollable Body Wrapper with Hidden Horizontal Scrollbar */}
      <div className="max-h-[65vh] overflow-y-auto overflow-x-hidden space-y-4 pr-1 pt-1">
        {/* Student Contact Info Card */}
        <div className="rounded-card border border-neutral-200 dark:border-neutral-800 bg-neutral-50/50 p-3.5 space-y-2 text-xs">
          <div className="flex justify-between items-center border-b border-neutral-200/60 pb-2">
            <span className="text-neutral-500 dark:text-neutral-400 font-medium">Student:</span>
            <span className="font-semibold text-neutral-900 dark:text-neutral-100">{name || "—"}</span>
          </div>

          {student?.admissionNo && (
            <div className="flex justify-between items-center border-b border-neutral-200/60 pb-2">
              <span className="text-neutral-500 dark:text-neutral-400 font-medium">Admission No:</span>
              <span className="text-neutral-800 dark:text-neutral-100 font-mono">{student.admissionNo}</span>
            </div>
          )}

          {(channel === "WHATSAPP" || channel === "SMS") && (
            <>
              <div className="flex justify-between items-center border-b border-neutral-200/60 pb-2">
                <span className="text-neutral-500 dark:text-neutral-400 font-medium">Phone:</span>
                <div>
                  {normalizedPhone ? (
                    <span className="font-mono text-neutral-900 dark:text-neutral-100">+{normalizedPhone}</span>
                  ) : (
                    <span className="text-danger-600 font-medium">Missing</span>
                  )}
                  {normalizedPhone && !contactOk() && (
                    <span className="text-danger-600 ml-1 font-medium">(invalid)</span>
                  )}
                </div>
              </div>

              {channel === "WHATSAPP" && normalizedPhone && (
                <div className="flex items-center justify-between pt-0.5">
                  <span className="text-neutral-500 dark:text-neutral-400 font-medium">On WhatsApp:</span>
                  <div className="flex items-center gap-1.5">
                    {waCheck.status === "checking" && (
                      <span className="text-neutral-500 dark:text-neutral-400">Checking…</span>
                    )}
                    {waCheck.status === "exists" && (
                      <Badge variant="success" className="gap-1">
                        <CheckCircle2 className="h-3 w-3" />
                        Verified
                      </Badge>
                    )}
                    {waCheck.status === "not_found" && (
                      <Badge variant="danger" className="gap-1">
                        <XCircle className="h-3 w-3" />
                        Not Registered
                      </Badge>
                    )}
                    {waCheck.status === "error" && (
                      <span className="text-amber-700">{waCheck.message}</span>
                    )}
                    {waCheck.status === "idle" && (
                      <span className="text-neutral-400 dark:text-neutral-500">
                        {appStatus === "CONNECTED" ? "—" : "Connect WhatsApp to verify"}
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={runWaCheck}
                      className="ml-1 text-[11px] font-medium text-primary-600 dark:text-primary-400 hover:underline inline-flex items-center gap-0.5"
                    >
                      <RefreshCw className="h-2.5 w-2.5" />
                      Re-check
                    </button>
                  </div>
                </div>
              )}
            </>
          )}

          {channel === "EMAIL" && (
            <div className="flex justify-between items-center">
              <span className="text-neutral-500 dark:text-neutral-400 font-medium">Email:</span>
              <span>{email || <span className="text-danger-600 font-medium">Missing</span>}</span>
            </div>
          )}
        </div>

        {/* Channel App Status */}
        {channel === "WHATSAPP" && (
          <div className="flex items-center justify-between rounded-card border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 p-2.5 text-xs">
            <span className="text-neutral-600 dark:text-neutral-300">
              App Connection Status:
            </span>
            <div className="flex items-center gap-2">
              <Badge variant={appStatus === "CONNECTED" ? "success" : "warning"}>
                {checking ? "Checking…" : appStatus}
              </Badge>
              <button
                type="button"
                onClick={checkConnection}
                className="text-[11px] font-medium text-primary-600 dark:text-primary-400 hover:underline"
              >
                Re-check
              </button>
            </div>
          </div>
        )}

        {channel !== "WHATSAPP" && (
          <Alert variant="warning" title={`${channelLabel} Unavailable`}>
            {channelLabel} connector is not ready yet. See{" "}
            <Link to={hubPath} className="underline font-medium">
              Notification apps
            </Link>
            .
          </Alert>
        )}

        {channel === "WHATSAPP" && appStatus !== "CONNECTED" && (
          <Alert variant="warning" title="WhatsApp Disconnected">
            WhatsApp is disconnected.{" "}
            <Link to={connectPath} className="underline font-medium">
              Connect WhatsApp
            </Link>{" "}
            or open{" "}
            <Link to={hubPath} className="underline font-medium">
              Notification apps
            </Link>
            .
          </Alert>
        )}

        {channel === "WHATSAPP" && !receiptId && (
          <>
            <Select
              label="Event"
              options={EVENT_OPTIONS}
              value={eventType}
              onChange={(val) => setEventType(val)}
            />

            {/* Outstanding Dues Ledger Card */}
            <div className="rounded-card border border-neutral-200 dark:border-neutral-800 p-3 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wide text-neutral-700 dark:text-neutral-200">
                  Outstanding Dues
                </span>
                <span className="text-sm font-bold text-neutral-900 dark:text-neutral-100">
                  {loadingLedger ? "…" : currency(outstandingTotal)}
                </span>
              </div>

              {loadingLedger ? (
                <div className="space-y-1.5 pt-1">
                  <Skeleton height={12} width="100%" />
                  <Skeleton height={12} width="80%" />
                </div>
              ) : dueLedgerRows.length === 0 ? (
                <p className="text-xs text-neutral-400 dark:text-neutral-500 py-1">
                  No published dues found for this student.
                </p>
              ) : (
                <div className="max-h-36 overflow-y-auto overflow-x-hidden pt-1 w-full">
                  <Table
                    columns={ledgerColumns}
                    data={dueLedgerRows}
                    size="sm"
                  />
                </div>
              )}
            </div>

            {/* Template Selection & Preview */}
            <div className="space-y-2">
              <Select
                label="Template"
                options={templateSelectOptions}
                value={selectedTemplateId}
                onChange={(val) => setSelectedTemplateId(val)}
                disabled={loadingTemplates || !templates.length}
              />

              {templatesToPreview.length > 0 && (
                <div className="space-y-2 max-h-36 overflow-y-auto">
                  {templatesToPreview.map((t) => (
                    <div
                      key={t._id}
                      className="rounded-card border border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-950 p-2.5 text-xs"
                    >
                      <p className="text-[11px] font-medium text-neutral-500 dark:text-neutral-400 mb-1">
                        {t.name}
                      </p>
                      <p className="whitespace-pre-wrap text-neutral-800 dark:text-neutral-100 font-mono text-[11px]">
                        {renderPreview(t.body, previewData)}
                      </p>
                    </div>
                  ))}
                </div>
              )}
              <p className="text-[11px] text-neutral-400 dark:text-neutral-500">
                Preview only — final amounts are recalculated server-side at send time.
              </p>
            </div>
          </>
        )}

        

        {results && (
          <div className="space-y-2">
            {results.map((r, idx) => (
              <Alert
                key={r.templateId || idx}
                variant={r.skipped ? "warning" : "success"}
                title={r.channel || channel}
              >
                {r.skipped ? (
                  <>Skipped: {r.reason || "unknown reason"}</>
                ) : (
                  <>
                    Queued for delivery{r.to ? ` to ${r.to}` : ""}. Check message logs for status.
                  </>
                )}
              </Alert>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default ChannelSendPanel;